import { useEffect, useId, type ReactNode } from 'react';
import type { DeliverableStatus, Metric } from '@pixerito/api-client';
import { formatDelta, STATUS_LABEL } from '../lib/format';

// ── Ícones de traço 1.5px ──
const ICONS = {
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />,
  logout: (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="m10 17-5-5 5-5M5 12h12" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  arrowUpRight: <path d="M7 17 17 7M8 7h9v9" />,
  arrowLeft: <path d="M19 12H5M11 18l-6-6 6-6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  link: (
    <>
      <path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
    </>
  ),
  close: <path d="M18 6 6 18M6 6l12 12" />,
  chevronLeft: <path d="m15 18-6-6 6-6" />,
  chevronRight: <path d="m9 18 6-6-6-6" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  download: (
    <>
      <path d="M12 4v11M7 11l5 5 5-5" />
      <path d="M5 20h14" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  trash: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
  check: <path d="m5 12 5 5 9-10" />,
  edit: <path d="M4 20h4L19 9l-4-4L4 16v4ZM13.5 6.5l4 4" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a1 1 0 0 1 1-1h10" />
    </>
  ),
} as const;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

export function Watermark({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return <span className={`wm ${className ?? ''}`} style={style} aria-hidden="true" />;
}

export function StatusPill({ status }: { status: DeliverableStatus }) {
  return (
    <span className={`status status--${status}`}>
      <i />
      {STATUS_LABEL[status]}
    </span>
  );
}

/** Variação vs. mês anterior. A cor segue se subir é bom para a métrica; seta e sinal dão o sentido. */
export function Delta({
  metric,
  compact = false,
  noneLabel = 'sem mês anterior',
}: {
  metric: Metric;
  compact?: boolean;
  /** Texto quando não há com o que comparar. */
  noneLabel?: string;
}) {
  if (metric.trend === 'none') {
    return <span className="delta delta--none">{compact ? '—' : noneLabel}</span>;
  }
  if (metric.trend === 'flat') return <span className="delta delta--flat">= igual</span>;
  const good = (metric.trend === 'up') === metric.higherIsBetter;
  const text = formatDelta(metric) ?? (metric.trend === 'up' ? 'subiu' : 'caiu');
  return (
    <span className={`delta ${good ? 'delta--good' : 'delta--bad'}`}>
      <span aria-hidden="true">{metric.trend === 'up' ? '▲' : '▼'}</span>
      <span className="sr-only">{metric.trend === 'up' ? 'Subiu' : 'Caiu'}</span>
      {text}
    </span>
  );
}

export function DeltaValue({ current, previous }: { current: number; previous: number | null | undefined }) {
  if (previous === null || previous === undefined) return <span className="delta delta--none">—</span>;
  if (previous === 0)
    return current === 0 ? (
      <span className="delta delta--flat">= igual</span>
    ) : (
      <span className="delta delta--good">
        <span aria-hidden="true">▲</span>novo
      </span>
    );
  const pct = Math.round(((current - previous) / previous) * 1000) / 10;
  if (pct === 0) return <span className="delta delta--flat">= igual</span>;
  const up = pct > 0;
  return (
    <span className={`delta ${up ? 'delta--good' : 'delta--bad'}`}>
      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
      {up ? '+' : '−'}
      {Math.abs(pct).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
    </span>
  );
}

export function Loading({ label = 'Carregando' }: { label?: string }) {
  return (
    <div className="row muted t-small" role="status" style={{ padding: '48px 0', justifyContent: 'center' }}>
      <span className="loader" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label}…
    </div>
  );
}

export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="notice notice--error" role="alert">
      <div className="stack" style={{ gap: 8 }}>
        <p>{message}</p>
        {onRetry && (
          <div>
            <button className="btn btn--ghost btn--sm" onClick={onRetry}>
              Tentar de novo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <Watermark />
      <p className="t-h3">{title}</p>
      {children && <p className="muted t-small">{children}</p>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="toast" role="status">
      <i />
      {message}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <label className="switch" htmlFor={id} style={disabled ? { opacity: 0.5, cursor: 'default' } : undefined}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="switch__track" aria-hidden="true" />
      <span className="stack" style={{ gap: 0 }}>
        <span style={{ fontWeight: 500, fontSize: 14 }}>{label}</span>
        {description && <span className="t-caption">{description}</span>}
      </span>
    </label>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

export function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
          <h2 className="t-h2">{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Fechar">
            <Icon name="close" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
